import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { StateService } from "../../../services/state.service";
import { AlertService } from "../../../services/alert.service";
import { UserprefService } from "../../../services/userpref.service";
import { S25FilestoreImageComponent } from "../../../standalone/s25.filestore.image.component";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25PipesModule } from "../../../pipes/s25.pipes.module";
import { S25Util } from "../../../util/s25-util";
import { ElectionApi, type BallotApiResponse } from "./election.api";
import { ElectionEligibleStore } from "./election.eligible.store";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import type { DropDownItem } from "../../../pojo/DropDownItem";
import { StateStore } from "../../../services/state/state.store";
import { InstanceCount } from "../../../util/s25.instance.count";

@TypeManagerDecorator("s25-ng-election-ballot")
@Component({
    selector: "s25-ng-election-ballot",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, S25FilestoreImageComponent, S25ButtonComponent, S25PipesModule, S25DropdownPaginatedModule],
    template: `
        <div class="c-padding--double election-ballot">
            @if (loading()) {
                <p>Loading ballot…</p>
            } @else if (error()) {
                <p class="c-text--error">{{ error() }}</p>
            } @else if (ballot()) {
                @if (eligibleElections().length > 1) {
                    <div class="election-ballot__election-picker c-margin-bottom--double">
                        <label class="election-ballot__picker-label" [id]="uniqueId">Election</label>
                        <s25-generic-dropdown
                            class="c-input"
                            [items]="eligibleElectionDropdownItems()"
                            [value]="activeElectionDropdownItem()"
                            (valueChange)="onElectionPickerChange($event)"
                            [placeholder]="'Switch election'"
                            [hideSearch]="true"
                            [ariaLabel]="{ by: uniqueId }"
                        />
                    </div>
                }
                <h1>{{ ballot()!.election.name }}</h1>
                @if (ballot()!.election.descr) {
                    <div class="c-margin-bottom--single" [innerHTML]="ballot()!.election.descr | safeHTML"></div>
                }
                <p>
                    Voting:
                    {{ ballot()!.election.votingStartDt | dateFormat: dateTimeFormatPreference }} –
                    {{ ballot()!.election.votingEndDt | dateFormat: dateTimeFormatPreference }}
                </p>
                @for (pos of displayPositions() || []; track pos.electionPositionId) {
                    <section class="c-margin-top--double election-ballot__position">
                        <h2>{{ pos.positionTitle }}</h2>
                        @if (pos.descr) {
                            <div [innerHTML]="pos.descr | safeHTML"></div>
                        }
                        <ul class="election-ballot__cands">
                            @for (c of pos.candidates; track c.contId) {
                                <li class="election-ballot__cand">
                                    <label class="election-ballot__label">
                                        <input
                                            type="radio"
                                            [name]="'pos-' + pos.electionPositionId"
                                            [checked]="selected()[pos.electionPositionId] === c.contId"
                                            (change)="onPick(pos.electionPositionId, c.contId)"
                                        />
                                        @if (c.photoUrl) {
                                            <div class="election-ballot__photo-wrap">
                                                <s25-ng-filestore-image
                                                    [value]="c.photoUrl"
                                                    [readOnly]="true"
                                                    [objectType]="-1"
                                                    [objectId]="-1"
                                                    class="election-ballot__photo"
                                                />
                                            </div>
                                        }
                                        <span class="election-ballot__name">{{ c.firstName }} {{ c.lastName }}</span>
                                    </label>
                                    @if (c.campaignBrief) {
                                        <div
                                            class="election-ballot__brief"
                                            [innerHTML]="c.campaignBrief | safeHTML"
                                        ></div>
                                    }
                                </li>
                            }
                        </ul>
                    </section>
                }
                <div class="c-margin-top--double election-ballot__actions">
                    <s25-ng-button [type]="'outline'" [onClick]="saveProgress" [disabled]="savingProgress()"
                        >Save Progress</s25-ng-button
                    >
                    <s25-ng-button [type]="'primary'" [onClick]="submitFinal">Submit Ballot</s25-ng-button>
                </div>
            }
        </div>
    `,
    styles: `
        .election-ballot__cands {
            list-style: none;
            padding: 0;
        }
        .election-ballot__cand {
            margin-bottom: 1rem;
            border: 1px solid #ddd;
            border-radius: 4px;
            padding: 0.75rem;
        }
        .election-ballot__label {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            cursor: pointer;
        }
        .election-ballot__photo-wrap {
            flex: 0 0 auto;
            width: 64px;
            height: 64px;
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px;
        }
        .election-ballot__photo-wrap ::ng-deep img.fs-image__preview {
            max-width: 64px;
            max-height: 64px;
        }
        .election-ballot__photo {
            max-width: 100%;
            max-height: 100%;
        }
        .election-ballot__name {
            flex: 1 1 auto;
            min-width: 0;
        }
        .election-ballot__brief {
            margin-top: 0.5rem;
            margin-left: 1.75rem;
        }
        .election-ballot__actions {
            display: flex;
            gap: 1rem;
        }
        .election-ballot__election-picker {
            max-width: 24rem;
        }
        .election-ballot__picker-label {
            display: block;
            margin-bottom: 0.35rem;
            font-weight: 600;
        }
        s25-generic-dropdown.c-input {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
            height: auto !important;
            min-height: 0 !important;
            padding: 0 !important;
        }
        s25-generic-dropdown.c-input .ngDropdownPaginated,
        s25-generic-dropdown.c-input .select2-container {
            box-sizing: border-box;
            min-width: 100%;
            width: 100%;
        }
    `,
})
export class ElectionBallotComponent {
    private static nextId = InstanceCount.idGenerator({ prefix: "ElectionBallot" });

    private readonly eligibleStore = inject(ElectionEligibleStore);
    private readonly stateStore = inject(StateStore);

    protected readonly uniqueId = ElectionBallotComponent.nextId();

    private readonly eligibleStoreReady = signal(false);

    readonly loading = signal(true);
    readonly error = signal<string | null>(null);
    readonly ballot = signal<BallotApiResponse | null>(null);
    readonly savingProgress = signal(false);
    readonly activeElectionId = signal<number | null>(null);

    readonly eligibleElections = computed(() => this.eligibleStore.elections());

    readonly eligibleElectionDropdownItems = computed((): DropDownItem[] =>
        this.eligibleElections().map((election) => ({
            itemId: election.electionId,
            itemName: election.name,
        })),
    );

    readonly activeElectionDropdownItem = computed((): DropDownItem | null => {
        const electionId = this.activeElectionId();
        if (!electionId) {
            return null;
        }
        const election = this.eligibleElections().find((row) => row.electionId === electionId);
        return {
            itemId: electionId,
            itemName: election?.name ?? `Election ${electionId}`,
            itemTypeId: undefined,
        };
    });

    /** positionId -> candidate cont_id */
    readonly selected = signal<Record<number, number>>({});

    /** From {@link UserprefService.getS25DateTimeformat}; used by `dateFormat` in template. */
    dateTimeFormatPreference = "";

    /**
     * Positions with candidate order applied once per ballot load (RANDOM does not reshuffle on every CD cycle).
     */
    readonly displayPositions = signal<BallotApiResponse["positions"] | null>(null);

    readonly routeElectionId = computed(() => this.stateStore.seElectionId());

    constructor() {
        void this.eligibleStore.refresh().finally(() => this.eligibleStoreReady.set(true));

        effect(() => {
            if (!this.eligibleStoreReady()) {
                return;
            }
            const electionId = this.routeElectionId();
            if (!electionId) {
                this.error.set("Missing election");
                this.loading.set(false);
                this.ballot.set(null);
                return;
            }
            if (electionId === this.activeElectionId()) {
                return;
            }
            void this.load(electionId);
        });
    }

    onElectionPickerChange = (item: DropDownItem | null) => {
        const nextElectionId = item?.itemId != null ? Number(item.itemId) : NaN;
        if (!nextElectionId || !Number.isFinite(nextElectionId)) {
            return;
        }
        if (nextElectionId === this.activeElectionId()) {
            return;
        }
        void this.load(nextElectionId);
        StateService.go("seElectionBallot", { electionId: nextElectionId }, { notify: false, reload: false });
    };

    private async load(electionId: number) {
        this.activeElectionId.set(electionId);
        this.loading.set(true);
        this.error.set(null);
        try {
            this.dateTimeFormatPreference = (await UserprefService.getS25DateTimeformat()) ?? "YYYY-MM-DD HH:mm";
            const data = await ElectionApi.getBallot(electionId);
            this.ballot.set(data);
            const order = data.election.candidateOrder;
            const positions = data.positions
                .map((pos) => {
                    const cands = [...pos.candidates];
                    if (order === "ALPHA") {
                        cands.sort((a, b) => (a.lastName || "").localeCompare(b.lastName || ""));
                    } else if (order === "RANDOM") {
                        S25Util.array.shuffle(cands);
                    } else {
                        cands.sort((a, b) => a.sortOrder - b.sortOrder);
                    }
                    return { ...pos, candidates: cands };
                })
                .sort((a, b) => a.sortOrder - b.sortOrder);
            this.displayPositions.set(positions);
            const sel: Record<number, number> = {};
            const map = data.selectedCandidateContIdByPosition || {};
            for (const k of Object.keys(map)) {
                sel[Number(k)] = map[k];
            }
            this.selected.set(sel);
        } catch (caughtError: any) {
            this.error.set(caughtError?.message || "Could not load ballot");
        } finally {
            this.loading.set(false);
        }
    }

    onPick = async (positionId: number, candidateContId: number) => {
        const b = this.ballot();
        if (!b) {
            return;
        }
        this.selected.update((m) => ({ ...m, [positionId]: candidateContId }));
        try {
            await ElectionApi.postVote(b.election.electionId, positionId, candidateContId);
        } catch (caughtError) {
            console.error(caughtError);
        }
    };

    saveProgress = async () => {
        const ballotData = this.ballot();
        if (!ballotData || this.savingProgress()) {
            return;
        }
        this.savingProgress.set(true);
        const selectedVotes = this.selected();
        // TODO: an N+1 but it's very likely small so leaving a TODO here to fix later:
        const votePromises = Object.entries(selectedVotes).map(([positionIdKey, candidateContId]) =>
            ElectionApi.postVote(ballotData.election.electionId, Number(positionIdKey), candidateContId),
        );
        try {
            const results = await Promise.allSettled(votePromises);
            const failed = results.filter((result) => result.status === "rejected");
            if (failed.length) {
                await AlertService.alert("Some votes could not be saved. Please try again.");
                return;
            }
            await AlertService.alert("Your ballot progress has been saved.");
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        } finally {
            this.savingProgress.set(false);
        }
    };

    submitFinal = async () => {
        const b = this.ballot();
        if (!b) {
            return;
        }
        const ok = await AlertService.confirm(
            "Once you submit, you cannot change your votes for this election. Continue?",
        );
        if (!ok) {
            return;
        }
        try {
            await ElectionApi.submit(b.election.electionId);
            await this.eligibleStore.refresh();
            StateService.go("seHomeView", {}, {});
        } catch (caughtError: any) {
            await AlertService.alert(caughtError?.message || "Submit failed");
        }
    };
}
